/**
 * Helpers for the admin's per-customer transaction history
 * (pages/admin/CustomerTransactions.jsx ← GET /api/admin/customers/{id}/transactions).
 *
 * A transaction is one row of the customer's wallet ledger: `amount` is
 * positive for money in and negative for money out, `type` is one of the
 * five kinds below, and `created_at` is a UTC timestamp. Everything shown to
 * the admin is in Indian time, whatever the device is set to.
 */
import { APP_TIME_ZONE } from "./istDate";

export const TRANSACTION_TYPES = [
  { value: "recharge", label: "Wallet top-up" },
  { value: "debit_delivery", label: "Delivery charge" },
  { value: "refund", label: "Refund" },
  { value: "adjustment", label: "Admin adjustment" },
  { value: "bonus", label: "Bonus" },
];

const LABELS = Object.fromEntries(TRANSACTION_TYPES.map((t) => [t.value, t.label]));

/** Label for a type; an unknown one (a kind added later) is shown readably
 *  rather than hidden. */
export const transactionLabel = (type) =>
  LABELS[type] || String(type || "Transaction").replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());

export const transactionsUrl = (customerId, { type = "", start = "", end = "", page = 1, pageSize = 20 } = {}) => {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (type) params.set("type", type);
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  return `/api/admin/customers/${encodeURIComponent(customerId)}/transactions?${params.toString()}`;
};

const rupees = (n) => Math.abs(Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** "+₹1,000" for money in, "−₹60" for money out, "₹0" for a free entry. */
export const signedAmount = (amount) => {
  const n = Number(amount || 0);
  if (n > 0) return `+₹${rupees(n)}`;
  if (n < 0) return `−₹${rupees(n)}`;
  return "₹0";
};

/** A balance, which may itself be negative: "₹550", "−₹92". */
export const balanceText = (amount) => `${Number(amount || 0) < 0 ? "−" : ""}₹${rupees(amount)}`;

export const amountTone = (amount) => {
  const n = Number(amount || 0);
  return n > 0 ? "in" : n < 0 ? "out" : "none";
};

/** "23 Sept 2026, 12:10 am" — date and time in IST. */
export const transactionWhen = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    timeZone: APP_TIME_ZONE, day: "2-digit", month: "short", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  });
};

/** "22 Sept" for a delivery's YYYY-MM-DD (a calendar date, no time zone). */
export const deliveryDay = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd || "");
  if (!m) return "";
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])).toLocaleDateString("en-IN", { timeZone: "UTC", day: "2-digit", month: "short" });
};
